import { CartUpdateEvent } from '@theme/events';
import { getCurrentCartRevision, recordCartRevision } from '@theme/cart-revision';
import { getMerchandiseCount } from '@theme/ffs-cart';
import { traceFfsDebug } from '@theme/syncsense-ffs-debug';

export const FFS_CART_RECONCILED_EVENT = 'syncsense:ffs-cart-reconciled';
export const SYNC_SENSE_CART_UPDATE_SOURCE = 'syncsense-ffs-reconciled';

/**
 * Translates a settled SyncSense cart into the theme's established cart update event.
 *
 * @param {CustomEvent} event
 * @param {Document} [target]
 */
export function handleFfsCartReconciled(event, target = document) {
  const detail = event.detail;
  const cart = detail?.cart;
  const revision = detail?.revision;

  if (!cart || !Number.isInteger(revision) || revision < 0 || detail?.changed !== true) return;

  traceFfsDebug({
    event: 'ffs-cart-reconciled-received',
    revision,
    operationId: detail?.operationId,
    eventName: FFS_CART_RECONCILED_EVENT,
    cart,
  });

  if (!recordCartRevision(revision)) {
    traceFfsDebug({
      event: 'ffs-cart-reconciled-discarded',
      revision,
      operationId: detail?.operationId,
      responseRevision: revision,
      currentRevision: getCurrentCartRevision() ?? revision,
      reason: 'stale-revision',
      cart,
    });
    return;
  }

  target.dispatchEvent(
    new CartUpdateEvent(cart, SYNC_SENSE_CART_UPDATE_SOURCE, {
      itemCount: getMerchandiseCount(cart),
      source: SYNC_SENSE_CART_UPDATE_SOURCE,
      revision,
    })
  );
}

/**
 * Installs the document-level FFS reconciliation bridge.
 *
 * @param {Document} [target]
 * @returns {() => void}
 */
export function installSyncSenseFfsCartBridge(target = document) {
  const handler = /** @param {Event} event */ (event) => {
    if (!(event instanceof CustomEvent)) return;
    handleFfsCartReconciled(event, target);
  };

  target.addEventListener(FFS_CART_RECONCILED_EVENT, handler);
  return () => target.removeEventListener(FFS_CART_RECONCILED_EVENT, handler);
}

if (typeof document !== 'undefined') {
  installSyncSenseFfsCartBridge();
}
